/**
 * GridCanvas
 *
 * Wraps react-grid-layout's ResponsiveGridLayout to render a widget canvas.
 * Registry-agnostic — the host page passes in a WidgetRegistry so Trading
 * widgets and Market Monitor widgets never appear on the same canvas.
 *
 * Optional canvas-drop integration: when `acceptedDropMimes` is non-empty,
 * the wrapper accepts HTML5 drops with one of those mimes that land on
 * empty canvas space (i.e. not on top of any existing widget). The host
 * page receives `(payload, mime, gridX, gridY)` via `onCanvasDrop` and
 * decides what to do (typically: create a new widget at that position).
 *
 * Widgets flagged `pinned` become static grid items: not draggable, not
 * resizable, and never pushed around by their neighbours.
 */

import React, { useCallback, useMemo, useRef } from 'react';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import { WidgetShell } from '../widget-shell/WidgetShell';
import {
  WIDGET_DRAG_HANDLE_CLASS,
  type WidgetPlacement,
  type WidgetRegistry,
} from '../grid-dashboard';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import styles from './GridCanvas.module.css';

interface RGLLayoutItem {
  i: string;
  x: number; y: number; w: number; h: number;
  minW?: number; maxW?: number; minH?: number; maxH?: number;
  static?: boolean;
}

interface RGLLayouts {
  [breakpoint: string]: RGLLayoutItem[];
}

const GRID_COLS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };
const ROW_HEIGHT = 40;
const GRID_MARGIN: [number, number] = [8, 8];

/**
 * Below this canvas width we switch to a single-column stacked layout with
 * drag/resize disabled — the desktop grid is unusable on a phone-sized
 * touch screen.
 */
const MOBILE_MAX_WIDTH = 768;

export interface CanvasDropArgs {
  payload: string;
  mime: string;
  gridX: number;
  gridY: number;
  event: React.DragEvent<HTMLDivElement>;
}

export interface GridCanvasProps {
  widgets: WidgetPlacement[];
  registry: WidgetRegistry;
  onLayoutChange: (widgets: WidgetPlacement[]) => void;
  onRemoveWidget: (instanceId: string) => void;
  emptyState?: React.ReactNode;
  /**
   * Mime types accepted by the canvas-level drop target. Drops with one of
   * these mimes that land on empty canvas space invoke `onCanvasDrop`.
   * Drops on top of an existing widget are not intercepted (the widget's
   * own drop handlers handle them).
   */
  acceptedDropMimes?: string[];
  onCanvasDrop?: (args: CanvasDropArgs) => void;
}

function placementsToLayout(
  widgets: WidgetPlacement[],
  registry: WidgetRegistry,
): RGLLayoutItem[] {
  return widgets.map((wp) => {
    const contract = registry.get(wp.widgetType);
    const item: RGLLayoutItem = {
      i: wp.instanceId, x: wp.x, y: wp.y, w: wp.w, h: wp.h,
      static: wp.pinned === true,
    };
    if (contract?.minSize) {
      item.minW = contract.minSize.width;
      item.minH = contract.minSize.height;
    }
    if (contract?.maxSize) {
      item.maxW = contract.maxSize.width;
      item.maxH = contract.maxSize.height;
    }
    return item;
  });
}

function layoutToPlacements(
  layout: RGLLayoutItem[],
  originalWidgets: WidgetPlacement[],
): WidgetPlacement[] {
  const widgetMap = new Map<string, WidgetPlacement>();
  for (const wp of originalWidgets) widgetMap.set(wp.instanceId, wp);
  return layout
    .map((item) => {
      const original = widgetMap.get(item.i);
      if (!original) return null;
      return { ...original, x: item.x, y: item.y, w: item.w, h: item.h };
    })
    .filter((wp): wp is WidgetPlacement => wp !== null);
}

/**
 * Build a single-column stacked layout for mobile: every widget spans the
 * full width of the active breakpoint and is laid out top-to-bottom in the
 * widgets' visual order (row first, then column). Size constraints (minW/
 * maxW) are intentionally dropped so a wide widget isn't forced past the
 * narrow column count.
 */
function stackedLayout(
  widgets: WidgetPlacement[],
  cols: number,
): RGLLayoutItem[] {
  let y = 0;
  return [...widgets]
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((wp) => {
      const item: RGLLayoutItem = { i: wp.instanceId, x: 0, y, w: cols, h: wp.h };
      y += wp.h;
      return item;
    });
}

const DEFAULT_EMPTY_STATE = (
  <div className={styles.emptyState}>
    <span className={styles.emptyIcon}>📊</span>
    <span className={styles.emptyTitle}>No widgets yet</span>
    <span className={styles.emptyDescription}>
      Click the &quot;Add widget&quot; button to start building your workspace.
    </span>
  </div>
);

/**
 * Pick the first mime in `accepted` that the drag carries. Returns null if
 * nothing matches (we ignore the drop in that case).
 */
function matchedDropMime(
  event: React.DragEvent<HTMLDivElement>,
  accepted: readonly string[],
): string | null {
  if (!accepted.length) return null;
  const types = event.dataTransfer.types;
  for (let i = 0; i < types.length; i += 1) {
    if (accepted.includes(types[i])) return types[i];
  }
  return null;
}

/**
 * True when the drop target sits inside an existing widget. We rely on the
 * react-grid-layout item wrapper class (`.react-grid-item`) which RGL puts
 * on every grid cell. Drops that fail this check are treated as
 * empty-canvas drops.
 */
function dropIsInsideWidget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('.react-grid-item') !== null;
}

/**
 * Map mouse coordinates to grid cell coordinates. Mirrors RGL's column
 * pitch so the new widget lands under the cursor (Q13 — snap to cursor
 * cell, host bumps down on collision).
 */
function mouseToGridCell(
  event: React.DragEvent<HTMLDivElement>,
  wrapper: HTMLDivElement,
  containerWidth: number,
  cols: number,
): { gridX: number; gridY: number } {
  const rect = wrapper.getBoundingClientRect();
  const localX = event.clientX - rect.left - GRID_MARGIN[0];
  const localY = event.clientY - rect.top - GRID_MARGIN[1];
  const pxPerCol = containerWidth / cols;
  const pxPerRow = ROW_HEIGHT + GRID_MARGIN[1];
  const gridX = Math.max(0, Math.min(cols - 1, Math.floor(localX / pxPerCol)));
  const gridY = Math.max(0, Math.floor(localY / pxPerRow));
  return { gridX, gridY };
}

export const GridCanvas: React.FC<GridCanvasProps> = ({
  widgets,
  registry,
  onLayoutChange,
  onRemoveWidget,
  emptyState,
  acceptedDropMimes,
  onCanvasDrop,
}) => {
  const { width: containerWidth, containerRef } = useContainerWidth({ initialWidth: 1280 });
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isMobile = containerWidth > 0 && containerWidth <= MOBILE_MAX_WIDTH;

  const layout = useMemo(
    () => placementsToLayout(widgets, registry),
    [widgets, registry],
  );

  const layouts: RGLLayouts = useMemo(() => {
    if (isMobile) {
      return {
        lg: stackedLayout(widgets, GRID_COLS.lg),
        md: stackedLayout(widgets, GRID_COLS.md),
        sm: stackedLayout(widgets, GRID_COLS.sm),
        xs: stackedLayout(widgets, GRID_COLS.xs),
        xxs: stackedLayout(widgets, GRID_COLS.xxs),
      };
    }
    return { lg: layout, md: layout, sm: layout, xs: layout, xxs: layout };
  }, [isMobile, widgets, layout]);

  const handleLayoutChange = useCallback(
    (currentLayout: RGLLayoutItem[]) => {
      // Mobile uses an auto-generated stacked layout; never persist it back
      // over the user's desktop arrangement.
      if (isMobile) return;
      const updated = layoutToPlacements(currentLayout, widgets);
      const changed = updated.some((wp, i) => {
        const orig = widgets[i];
        if (!orig) return true;
        return wp.x !== orig.x || wp.y !== orig.y || wp.w !== orig.w || wp.h !== orig.h;
      });
      if (changed || updated.length !== widgets.length) {
        onLayoutChange(updated);
      }
    },
    [widgets, onLayoutChange, isMobile],
  );

  const dropEnabled = !!onCanvasDrop && !!acceptedDropMimes && acceptedDropMimes.length > 0;

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!dropEnabled) return;
      if (dropIsInsideWidget(event.target)) return;
      const mime = matchedDropMime(event, acceptedDropMimes!);
      if (!mime) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    [dropEnabled, acceptedDropMimes],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!dropEnabled || !onCanvasDrop) return;
      if (dropIsInsideWidget(event.target)) return;
      const mime = matchedDropMime(event, acceptedDropMimes!);
      if (!mime) return;
      const payload = event.dataTransfer.getData(mime);
      if (!payload) return;
      event.preventDefault();
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const { gridX, gridY } = mouseToGridCell(event, wrapper, containerWidth, GRID_COLS.lg);
      onCanvasDrop({ payload, mime, gridX, gridY, event });
    },
    [dropEnabled, onCanvasDrop, acceptedDropMimes, containerWidth],
  );

  const setRefs = useCallback(
    (el: HTMLDivElement | null) => {
      wrapperRef.current = el;
      // useContainerWidth's containerRef may be either a callback ref or a
      // MutableRefObject depending on the package version — handle both so
      // we don't clobber its measurement plumbing.
      const cr = containerRef as unknown;
      if (typeof cr === 'function') {
        (cr as (n: HTMLDivElement | null) => void)(el);
      } else if (cr && typeof cr === 'object' && 'current' in (cr as object)) {
        (cr as { current: HTMLDivElement | null }).current = el;
      }
    },
    [containerRef],
  );

  if (widgets.length === 0) {
    return (
      <div
        className={styles.canvasWrapper}
        ref={setRefs}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {emptyState ?? DEFAULT_EMPTY_STATE}
      </div>
    );
  }

  return (
    <div
      className={styles.canvasWrapper}
      ref={setRefs}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ResponsiveGridLayout
        width={containerWidth}
        layouts={layouts}
        cols={GRID_COLS}
        rowHeight={ROW_HEIGHT}
        margin={GRID_MARGIN}
        compactType="vertical"
        draggableHandle={`.${WIDGET_DRAG_HANDLE_CLASS}`}
        // Don't start a drag when the user clicks an interactive element
        // inside a widget body (inputs, buttons, links, contenteditable,
        // etc.). Without this, marking a whole card as the drag handle
        // would intercept clicks meant for those controls.
        draggableCancel="input, textarea, select, button, a, svg, [contenteditable='true']"
        isResizable={!isMobile}
        isDraggable={!isMobile}
        useCSSTransforms
        onLayoutChange={handleLayoutChange}
      >
        {widgets.map((wp) => {
          const contract = registry.get(wp.widgetType);
          if (!contract) {
            console.warn(`[GridCanvas] Unknown widgetType "${wp.widgetType}" — skipping`);
            return null;
          }
          const WidgetComponent = contract.component;
          return (
            <div key={wp.instanceId}>
              <WidgetShell
                instanceId={wp.instanceId}
                widgetType={wp.widgetType}
                registry={registry}
                onRemove={onRemoveWidget}
              >
                <WidgetComponent instanceId={wp.instanceId} config={wp.config} />
              </WidgetShell>
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
};

export default GridCanvas;
